<!--
  Send Invites tab — the body of the old Textinvites.vue minus the inline history block
  (now its own tab, TextInviteHistory.vue). Carries the June '26 purpose-select work
  (purpose in the INVITE_VIA_TEXT payload + SMS purpose preview) and the BYO-sending
  additions: own-Twilio banner, hidden credit UI when BYO is active, byoautherror surfacing.
-->
<template>
<div>
  <PurposeSelect />
  <!-- PURPOSE LINKS: show what the purpose text will roughly look like -->
  <div v-if="purpose !== 'general' && !purposeBlocked" class="p-4 my-3 rounded" style="background:#f0f4ff;">
    <p class="font-medium mb-1">Text preview</p>
    <p class="text-dark">{{ smsPreviewText }}</p>
  </div>

  <!-- BYO SENDING: texts go out via the business's own Twilio account — no credits -->
  <div v-if="byoTextActive" class="p-4 my-3 text-lg font-medium rounded flex items-center lightbrandbg">
    <feather-icon icon="SendIcon" svgClasses="stroke-current text-light w-6 h-6" class="mx-2" />
    <span>
      <p>Sending via your own Twilio account — invites don't use Feedback Link credits.<template v-if="!$store.getters['dash/isCollaborator']"> <a href="" @click.prevent="$router.push('/dash/settings/sending')">Sending settings ></a></template></p>
    </span>
  </div>

  <div class="vx-row">

    <!-- CARD 1: CONGRATS -->
    <div class="vx-col w-full mb-base flex" :class="{'lg:w-3/4': !byoTextActive}">
      <vx-card class="greet-user overflow-auto pb-5" title="ENTER RECIPIENTS">

        <div>
          <p><span class="font-medium">Recipients entered below will be addressed personally by name.</span></p>
          <div v-for="(contact, i) in specifiedContacts" :key="String('cntc' + i)" class="w-full my-4">
            <div class="md:flex w-full">
              <vs-input class="mr-2 w-full md:w-4/12" placeholder="First name" :name="`First name ${i + 1}`"
                v-model="contact.name" v-validate="'required|max:50'" />
              <vs-input class="mr-2 w-full md:w-4/12" @input="() => {specifiedContacts[i].error = false}" placeholder="Enter number, e.g. +14155551234"
                :name="`Number ${i + 1}`" v-model="contact.number" />
              <vs-button class="w-full md:w-10" type="border" icon-pack="feather" icon="icon-trash" @click="() => {
                specifiedContacts.splice(i, 1)
              }"></vs-button>
            </div>
            <span class="block mt-2 text-danger" v-if="errors.first(`First name ${i + 1}`)">Please enter a valid first name not longer than 50 characters.</span>
            <span class="block mt-2 text-danger" v-if="contact.error">Please enter a valid number in the E164 format.</span>
          </div>
          <vs-button type="border" icon-pack="feather" icon="icon-plus-circle" size="small" @click="() => {
            specifiedContacts.push({
              name: '',
              number: ''
            })
          }">Add</vs-button>
        </div>

        <vs-divider class="my-6" />
          <p class="my-2">You may also include a list of just numbers.</p>
          <p><span class="font-medium">Numbers entered below will trigger text invites without a personal greeting. We highly recommend entering names (using the boxes above), so that invites are more personal.</span></p>
        <p class="my-2">Seperate numbers with commas or spaces:</p>
        <vs-textarea v-model.trim="recipients" class="w-full" height="200px" placeholder="+14155551234, +442071234567 +10124567890, +442070987654" />
        <div class="my-2 flex items-center">
          <feather-icon icon="InfoIcon" svgClasses="stroke-current text-dark w-6 h-6" class="mx-2" />
          <span>Numbers should be in the standard international E164 format, and may not contain spaces or hyphens (-). Valid examples are: +14155552671 (USA) or +442071838750 (Great Britain). Read more about the <a href="https://www.twilio.com/docs/glossary/what-e164" target="_blank">E164 format</a>.</span>
        </div>
        <vs-button class="float-right my-2 w-full md:w-auto" size="large" style="min-width: 250px;" :disabled="purposeBlocked" @click="submitThis">Send</vs-button>
        <vs-button class="float-right m-2 w-full hidden md:inline-block md:w-auto" size="large" type="border" @click="this.recipients = ''">Clear</vs-button>
        <p class="text-danger mt-5" v-if="message" v-html="message"></p>
        <p class="text-danger mt-5" v-if="error">{{ error }}</p>
      </vx-card>
    </div>

    <div class="vx-col w-full lg:w-1/4 mb-base flex" v-if="!byoTextActive">
      <div class="text-center w-full">
        <vx-card class="mb-5" v-if="hasMonthlyQuota || isTrial">
          <p>Credits left</p>
          <p class="font-medium text-3xl my-2 text-primary">{{ textQuota }}</p>
          <p class="text-dark">{{ currentMonthOrTrial }}</p>
        </vx-card>
        <vx-card v-if="addonTextCredits">
          <p>{{ hasMonthlyQuota ? 'Addon credits' : 'Credits left' }}</p>
          <p class="font-medium text-3xl my-2 text-primary">{{ addonTextCredits }}</p>
          <vs-button class="my-2 w-full" @click="() => {showAddCredits = true}">+ Add More</vs-button>

         <vs-popup class="holamundo" title="Purchase add-on credits" :active.sync="showAddCredits">
         <h5 class="my-2">Credit cost</h5>
         <vs-table max-items="20" :data="[
          {
            credits: '500',
            cost: '$9',
          },
          {
            credits: '2000',
            cost: '$29',
          },
          {
            credits: '5000',
            cost: '$69',
          },
          {
            credits: '10000',
            cost: '$119',
          }
          ]" stripe >
            <template slot="thead">
              <vs-th>Credits</vs-th>
              <vs-th>Cost</vs-th>
              <vs-th></vs-th>
            </template>
            <template slot-scope="{data}">
              <vs-tr :key="indextr" v-for="(tr, indextr) in data">
                <vs-td :data="data[indextr].credits">
                  {{ data[indextr].credits }}
                </vs-td>
                <vs-td :data="data[indextr].cost">
                  {{ data[indextr].cost}}
                </vs-td>
                <vs-td :data="data[indextr].cost">
                  <vs-button class="float-right ml-1" @click="purchaseCredits(data[indextr].credits)">Checkout ></vs-button>
                </vs-td>
              </vs-tr>
            </template>
          </vs-table>
          <div class="con-vs-alert mt-3 con-vs-alert-success">
            <h4 class="titlex vs-alert--title">Rollover of credits</h4>
          <div class="vs-alert text-lg">
          Unused credits will roll over each month until it is used.
        </div>
        </div>
          <vs-collapse type="margin" :open-hover="true" class="small" >
                <vs-collapse-item icon-pack="feather" icon-arrow="icon-help-circle" class="text-left">
                  <div slot="header">
                    Payment securely processed by Stripe.
                  </div>
      <div class="my-10 w-full">
        <img src="@/assets/images/stripe.png" class="h-10 mx-auto block"/>
      </div>
      <div class="my-6 mx-auto w-full text-center">
        <p class="my-2 max-w-xs mx-auto text-sm">We accept Visa, MasterCard, American Express, Discover and Diner’s Club.</p>
        <img src="@/assets/images/paymentmethods.png" class="w-80 mx-auto"/>
      </div>
      <div class="max-w-md mx-auto">
      <p class="mt-5 font-medium">TRANSACTION SECURITY</p>
      <p>All transaction information passed between Feedback Link and Stripe's systems is encrypted using 256-bit SSL certificates. No cardholder information is ever passed unencrypted and any messages sent to our servers from Stripe are signed using MD5 hashing to prevent tampering.</p>

      <p class="mt-5 font-medium">ENCRYPTION & DATA STORAGE</p>
      <p>All sensitive data stored by Stripe Systems is secured using the same internationally recognised 256-bit encryption standards used by, among others, the US Government. The encryption keys are held on state-of-the-art, tamper proof systems in the same family as those used to secure VeriSign's Global Root certificate, making them all but impossible to extract. The data Stripe hold is extremely secure and they are regularly audited by banks and banking authorities to ensure it remains so.</p>

      <p class="mt-5">Feedback Link does not store any payment details. For more information please read our privacy policy.</p>
      </div>
                </vs-collapse-item>
              </vs-collapse>
        <vs-divider class="my-4"/>
        <div class="overflow-auto mt-4">
          <vs-button type="border" class="float-right" @click="() => {showAddCredits = false}">Cancel</vs-button>
        </div>
      </vs-popup>

        </vx-card>
      </div>
    </div>


  </div>
  <div class="vx-row">
    <div class="vx-col w-full lg:w-4/12 mb-base flex" v-if="!$store.getters['dash/isAgencyClientParentEID']">
      <vx-card title="Example text/SMS">
        <p class="my-2">An example of what recipients will see.</p>
        <img src="@/assets/images/misc/invites.png" class="w-full mx-auto max-w-sm" />
      </vx-card>
    </div>
    <div class="vx-col w-full lg:w-5/12 mb-base flex" v-if="!byoTextActive">
        <vx-card title="Credit costs">
          <p class="my-2">Credit cost for each invite sent, per destination country:</p>
          <vs-table max-items="20" :data="[
          {
            country: 'USA & Canada',
            cc: '1',
            dc: '+1'
          },
          {
            country: 'Australia',
            cc: '3',
            dc: '+61'
          },
          {
            country: 'United Kingdom',
            cc: '4',
            dc: '+44'
          },
          {
            country: 'Norway',
            cc: '7',
            dc: '+47'
          },
          {
            country: 'Sweden',
            cc: '7',
            dc: '+46'
          },
          {
            country: 'Norway',
            cc: '7',
            dc: '+47'
          },
          {
            country: 'Ireland',
            cc: '7',
            dc: '+353'
          },
          {
            country: 'Germany',
            cc: '8',
            dc: '+49'
          },
          {
            country: 'New Zealand',
            cc: '11',
            dc: '+64'
          },
          {
            country: 'All other countries',
            cc: '6',
            dc: 'All others'
          }
          ]" stripe >
            <template slot="thead">
              <vs-th>Country</vs-th>
              <vs-th>Dialing Code</vs-th>
              <vs-th>Credit Cost</vs-th>
            </template>
            <template slot-scope="{data}">
              <vs-tr :key="indextr" v-for="(tr, indextr) in data">
                <vs-td :data="data[indextr].dateSent">
                  {{ data[indextr].country }}
                </vs-td>
                <vs-td :data="data[indextr].to">
                  {{ data[indextr].dc}}
                </vs-td>
                <vs-td :data="data[indextr].to">
                  {{ data[indextr].cc}}
                </vs-td>
              </vs-tr>
            </template>
          </vs-table>
        </vx-card>
    </div>
  </div>
</div>
</template>


<script>


/**/
import PurposeSelect from '../components/ptcomponents/PurposeSelect.vue'
import {
  findPhoneNumbers,
  getCountryCallingCode
} from 'libphonenumber-js'

function getUniques (a) {
  const seen = {}
  return a.filter(function (item) {
    return seen.hasOwnProperty(item.number) ? false : (seen[item.number] = true)
  })
}

//EN fallbacks for the SMS preview — custom (cloned) language packs never receive keys
//added after cloning (same pattern as the backend lp() helper)
const SMS_EN_FALLBACKS = {
  purposeReviewTextInvite: 'We’d love it if you left us a review. Follow this secure link:',
  purposeReferralTextInvite: 'Know someone who’d love us? Refer a friend via this secure link:'
}

export default {
  computed: {
    byoTextActive () {
      return this.$store.getters['dash/byoTextActive']
    },
    purpose () {
      return this.$store.state.dash.collectPurpose.purpose
    },
    purposeBlocked () {
      return this.$store.getters['dash/purposeBlocked']
    },
    smsPreviewText () {
      const pack = this.$store.getters['dash/currentLanguagePack']
      const key = this.purpose === 'referral' ? 'purposeReferralTextInvite' : 'purposeReviewTextInvite'
      const line = (pack && typeof pack[key] === 'string' && pack[key].trim() !== '') ? pack[key] : SMS_EN_FALLBACKS[key]
      //mirrors inviteViaText's no-name message: "Business - <line> <link>?purpose=..."
      //(SMS links omit &platforms= to keep the message short). The token joins with '-' on
      //feedback.link/givefdbck.com but with '/' on a custom domain — same as the sender's
      //fbl/fbe vs dmn branches.
      const businessName = this.$store.state.dash.viewData.business.businessName
      const tokenJoin = this.$store.getters['dash/hasCustomDomainString'] ? '/' : '-'
      const link = `${this.$store.getters['dash/feedbackURL']}${tokenJoin}TOKEN?purpose=${this.purpose}`
      return `${businessName} - ${line} ${link}`
    },
    addonTextCredits () {

      if (this.isTrial) return false
      if (!this.businessQuotas) return false
      const addonTextCredits = this.businessQuotas.addonTextCredits
      return typeof addonTextCredits === 'undefined' ? false : addonTextCredits

    },
    hasMonthlyQuota () {

      if (!this.businessQuotas) return false
      return typeof this.businessQuotas.textQuota !== 'undefined'

    },
    businessQuotas () {
      const quotas = this.$store.state.dash.viewData.business.inviteQuotas
      return typeof quotas !== 'undefined' ? quotas : false
    },
    isTrial () {
      return this.$store.getters['dash/subscription'].isTrial
    },
    textQuota () {
      if (this.isTrial) {
        return `${this.$store.state.dash.viewData.business.trialTextQuotaLeft  } credits`
      } else {
        const d = new Date()
        if (d.getMonth() === this.businessQuotas.quotasCurrentMonth) {
          return `${this.businessQuotas.textQuotaCurrent  }/${  this.businessQuotas.textQuota}`
        } else {
          return `${this.businessQuotas.textQuota  }/${  this.businessQuotas.textQuota}`
        }
      }
    },
    currentMonthOrTrial () {
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
      const d = new Date()
      return this.isTrial ? 'in free trial' : `for ${  monthNames[d.getMonth()]}`
    },
    logoUrl: {
      set (v) {
        this.$store.commit('dash/MUTATE_APPEARANCE_INFO', {
          what: 'logoUrl',
          val: v
        })
      },
      get () {
        return this.$store.state.dash.viewData.business.publicFeedbackDetail.appearanceInfo.logoUrl
      }
    },
    feedbackAddress: {
      set (v) {
        this.$store.commit('dash/MUTATE_FEEDBACKLINK', v.replace(/[^a-zA-Z0-9]/g, ''))
      },
      get () {
        return this.$store.state.dash.viewData.business.publicFeedbackDetail.feedbackAddressHandle
      }
    },
    colorHex: {
      set (v) {
        this.$store.commit('dash/MUTATE_APPEARANCE_INFO', {
          what: 'backgroundHex',
          val: v
        })
      },
      get () {
        return this.$store.state.dash.viewData.business.publicFeedbackDetail.appearanceInfo.backgroundHex
      }
    }
  },
  methods: {
    purchaseCredits (numberOfCredits) {

      const creditsCode = `${numberOfCredits}c`

      const data = {
        numberOfCredits: creditsCode,
        customerEmail: this.$store.getters['auth/userEmail']
      }

      this.$vs.loading()

      this.$store.dispatch('dash/INITIATE_TEXT_CREDITS_ADDON', data).then((r) => {

        //console.log('got response')
        //console.log(r)

        window.location.href = r.url

        this.$vs.loading.close()

      }).catch((e) => {

        this.$vs.loading.close()

        this.$vs.notify({
          title: 'Purchase credits',
          text: 'Something went wrong, and we couln\'t initiate the purchase process. Please try again or reach out to support.',
          color: 'danger',
          time: 20000,
          iconPack: 'feather',
          icon: 'icon-info'
        })


      })


    },
    submitForm (recipients) {

      //noconsoleconsole.log('submitting text')

      const data = {
        handle: this.$store.state.dash.viewData.business.feedbackAddressHandle,
        businessName: this.$store.state.dash.viewData.business.businessName,
        businessID: this.$store.state.dash.viewData.business.businessID,
        recipients
      }

      //PURPOSE LINKS: aim this batch at a review/referral request (inviteViaText swaps the
      //invite line and appends ?purpose= to each link; no platforms — SMS stays short)
      if (this.purpose !== 'general') {
        data.purpose = this.purpose
      }

      //console.log(recipients)
      //return false

      if (this.$store.getters['dash/isAgencyClientParentEID']) data.isAgencyClient = true

      this.$vs.loading()

      this.$store.dispatch('dash/INVITE_VIA_TEXT', data).then((r) => {

        //noconsoleconsole.log('got response')
        //noconsoleconsole.log(r)

        //BYO SENDING: a failed send through the business's own Twilio account comes back
        //as an object with the raw provider error (recipient #1 failed -> nothing was sent)
        if (r.data && typeof r.data === 'object' && r.data.status === 'byoautherror') {
          this.error = `We couldn't send via your own Twilio account — Twilio said: "${r.data.detail}". No invites were sent. Check your details under Settings > Email & Text Sending (use the test button), or switch back to sending with Feedback Link.`
          this.$vs.loading.close()
          return
        }

        switch (r.data) {
        case 'quotareachedtrial':
          this.error = 'We couldn\'t send your invites because you have reached your trial quota for sending text/SMS invites. Choose a plan to send more!'
          break
        case 'quotareached':
          this.error = 'We couldn\'t send your invites because you have reached your quota for sending texts/SMSs this month. You can upgrade your plan if you want to send more, or you can wait for next month.'
          break
        case 'success':
          this.$vs.notify({
            title: 'Invites sent',
            text: 'Your invites have been sent!',
            color: 'success',
            time: 9000,
            iconPack: 'feather',
            icon: 'icon-check'
          })
          this.recipients = ''
            this.specifiedContacts = [{
              name: '',
              number: '',
            },
            {
              name: '',
              number: ''
            }
            ]
          break
        default:
          this.error =
              'We couldn\'t send your invites - something went wrong with processing your list. Please double check your list of recipients and make sure numbers are in the right format. If you continue having problems, please reach out to support.'
        }

        this.$vs.loading.close()

      }).catch((e) => {


        this.$vs.loading.close()
        this.error = 'We couldn\'t send your invites - something went wrong with processing your list. Please double check your list of recipients. If you continue having problems, please reach out to support.'


      })

    },
    iDontWantToAddCredits () {

    },
    submitThis () {

      this.message = false
      this.error = false

      const recipientsValid = this.formValid()
      //noconsoleconsole.log(recipientsValid)

      if (recipientsValid) {

        //noconsoleconsole.log('numbers:')
        //noconsoleconsole.log(recipientsArrayFormatted)

        this.submitForm(recipientsValid)
        // CHECK QUOTAS AND THEN POST TO SERVER

      } else {
        this.message =
          'There is an error in the numbers you entered, make sure numbers are seperated by spaces and that they\'re in the standard E164 format. <a class="link" href="https://support.twilio.com/hc/en-us/articles/223183008-Formatting-International-Phone-Numbers" target="_blank">Learn about E164 numbers.</a>'
      }
    },
    formValid () {

      //noconsoleconsole.log('checking sms numbers')

      //fetch valid numbers from textarea
      let list =
        findPhoneNumbers(this.recipients).map(
          (itm) => (
            {name: false, number: `+${  getCountryCallingCode(itm.country)  }${itm.phone}`})
          )

      //validate numbers from recipients

      this.specifiedContacts.forEach((sC,i) => {
        if ((sC.name.trim() !== '') && (sC.number.trim() !== '')) {

          if (findPhoneNumbers(sC.number).length === 0) {
            this.specifiedContacts[i].error = true
          } else {
            list.push({
              name: sC.name,
              number: sC.number
            })
          }

        }
      })

      list = getUniques(list)

      if (list.length === 0) {
        this.message =
          'You haven\'t entered any valid numbers. If you have, make sure numbers are seperated by spaces and that they\'re in the standard E164 format. <a class="link" href="https://support.twilio.com/hc/en-us/articles/223183008-Formatting-International-Phone-Numbers" target="_blank">Learn about E164 numbers.</a>'
        return false
      }

      if (list.length > 1000) {
        this.message = `You can only send to 1,000 numbers at a time. You have entered ${  list.length  } numbers`
        return false
      }

      return list

    }
  },
  data () {
    return {
      specifiedContacts: [{
        name: '',
        number: '',
        error: false
      }, {
        name: '',
        number: '',
        error: false
      }],
      recipients: '',
      message: false,
      error: false,
      showAddCredits: false
    }
  },
  components: {
    PurposeSelect
  },
  created () {

    const currentURL = new URL(window.location.href)
    if (currentURL.searchParams.get('newcredits')) {
      const credits = currentURL.searchParams.get('newcredits').replaceAll('c', '')
      this.$vs.notify({
        title: 'New credits purchased',
        text: `You\'ve successfully purchased ${credits} credits. It will reflect in your dashboard in the next minute.`,
        color: 'success',
        time: 60000,
        iconPack: 'feather',
        icon: 'icon-check'
      })
    }

  }
}
</script>

<style lang="scss">
/*! rtl:begin:ignore */
#dashboard-analytics {
    .greet-user {
        position: relative;

        .decore-left {
            position: absolute;
            left: 0;
            top: 0;
        }
        .decore-right {
            position: absolute;
            right: 0;
            top: 0;
        }
    }

    @media(max-width: 576px) {
        .decore-left,
        .decore-right {
            width: 140px;
        }
    }
}
/*! rtl:end:ignore */
</style>
